/**
 * Core chat type definitions for the `app/app` chat subsystem.
 *
 * Restored from the pre-Vite-migration implementation (git history) and merged
 * with fields added afterwards by real, already-deployed backend integrations
 * (chat session rename/delete/create, agent save) so neither generation regresses.
 * See ui/specs/bugs/ui-v3/BUG-UI-V3-009-chat-session-store-shape-mismatch.md.
 */
import {
  WorkplaceDocument,
  Filters,
  SearchWorkplaceDocument,
  StreamStopReason,
} from "@/shared/lib/search/interfaces";
import { Packet } from "./services/streamingModels";

export type FeedbackType = "like" | "dislike";
export type ChatState =
  | "input"
  | "loading"
  | "streaming"
  | "toolBuilding"
  | "uploading";
export interface RegenerationState {
  regenerating: boolean;
  finalMessageIndex: number;
}

export enum RetrievalType {
  None = "none",
  Search = "search",
  SelectedDocs = "selectedDocs",
}

export enum ResearchType {
  LegacyAgentic = "LEGACY_AGENTIC",
  Thoughtful = "THOUGHTFUL",
  Deep = "DEEP",
  Fast = "FAST",
}

export enum ChatSessionSharedStatus {
  Private = "private",
  Public = "public",
  LinkOnly = "link_only", // NEW — CR-027 Change 1, khớp domainchat.SharedStatusLinkOnly (backend SOL-027 §2)
}

// Shape used by the real (already-deployed) chat search feature —
// GET /api/chat/search (CR-027 Change 3, backend SOL-027 §2).
export interface ChatSearchItem {
  id: number;
  name: string;
  time_created: string;
}

export interface ChatSearchGroup {
  label: string;
  chats: ChatSearchItem[];
}

export interface ChatSearchResponse {
  groups: ChatSearchGroup[];
  has_more: boolean;
}

export const BUFFER_COUNT = 35;

export interface RetrievalDetails {
  run_search: "always" | "never" | "auto";
  real_time: boolean;
  filters?: Filters;
  enable_auto_detect_filters?: boolean | null;
}

// Citation number -> Document ID (allows O(1) lookup when rendering citations)
export type CitationMap = { [citation_num: number]: string };

export enum ChatFileType {
  IMAGE = "image",
  DOCUMENT = "document",
  PLAIN_TEXT = "plain_text",
  TABULAR = "tabular",
  USER_KNOWLEDGE = "user_knowledge",
}

export const isTextFile = (fileType: ChatFileType) =>
  [
    ChatFileType.PLAIN_TEXT,
    ChatFileType.TABULAR,
    ChatFileType.USER_KNOWLEDGE,
    ChatFileType.DOCUMENT,
  ].includes(fileType);

export interface FileDescriptor {
  id: string;
  type: ChatFileType;
  name?: string | null;

  user_file_id?: string | null;
  // FE only
  isUploading?: boolean;
}

export interface FileDescriptorWithHighlights extends FileDescriptor {
  match_highlights: string[];
}

export interface LLMRelevanceFilterPacket {
  relevant_chunk_indices: number[];
}

export interface ToolCallMetadata {
  tool_name: string;
  tool_args: Record<string, any>;
  tool_result?: Record<string, any>;
}

export interface ToolCallFinalResult {
  tool_name: string;
  tool_args: Record<string, any>;
  tool_result: Record<string, any>;
}

export interface ChatSession {
  id: string;
  name: string;
  persona_id?: number | null;
  time_created: string;
  time_updated?: string;
  shared_status?: ChatSessionSharedStatus | "public" | "private" | "link_only";
  project_id?: number | string | null;
  folder_id?: number | null;
  current_alternate_model?: string | null;
  current_temperature_override?: number | null;
  description?: string | null;
}

export interface SearchSession {
  search_session_id: string;
  documents: SearchWorkplaceDocument[];
  messages: BackendMessage[];
  description: string;
}

export interface Message {
  is_generating?: boolean;
  messageId?: number | null;
  nodeId: number; // Unique identifier for tree structure (can be negative for temp messages)
  message: string;
  type: "user" | "assistant" | "system" | "error"; // TODO: rename "assistant" to "agent"
  retrievalType?: RetrievalType;
  researchType?: ResearchType;
  query?: string | null;
  files: FileDescriptor[];
  toolCall: ToolCallMetadata | null;
  // for rebuilding the message tree - these now use nodeId
  parentNodeId: number | null;
  childrenNodeIds?: number[];
  latestChildNodeId?: number | null;
  alternateAgentID?: number | null;
  stackTrace?: string | null;
  errorCode?: string | null;
  isRetryable?: boolean;
  errorDetails?: Record<string, any> | null;
  overridden_model?: string;
  stopReason?: StreamStopReason | null;

  // NEW — CR-023. modelIndex ưu tiên đọc từ backend (persist), modelProvider
  // dùng để tra icon mà không cần đoán qua modelProviderLookup theo tên.
  modelIndex?: number | null;
  modelProvider?: string | null;

  // Multi-model answer generation
  preferredResponseId?: number | null;
  modelDisplayName?: string | null;

  // new gen
  packets: Packet[];
  packetCount?: number; // Tracks packet count for React memo comparison (avoids reading from mutated array)

  // cached values for easy access
  documents?: WorkplaceDocument[] | null;
  citations?: CitationMap;

  // feedback state
  currentFeedback?: FeedbackType | null;

  // Duration in seconds for processing this message (agent messages only)
  processingDurationSeconds?: number;
}

export interface BackendChatSession {
  chat_session_id: string;
  description: string;
  persona_id: number;
  persona_name?: string;
  messages: BackendMessage[];
  time_created: string;
  time_updated: string;
  shared_status: ChatSessionSharedStatus;
  current_temperature_override: number | null;
  current_alternate_model?: string;

  owner_name?: string | null;
  packets: Packet[][];
}

export function toChatSession(backend: BackendChatSession): ChatSession {
  return {
    id: backend.chat_session_id,
    name: backend.description,
    persona_id: backend.persona_id,
    time_created: backend.time_created,
    time_updated: backend.time_updated,
    shared_status: backend.shared_status,
    project_id: null,
    current_alternate_model: backend.current_alternate_model ?? "",
    current_temperature_override: backend.current_temperature_override,
    description: backend.description,
  };
}

export interface BackendMessage {
  message_id: number;
  message_type: string;
  research_type?: string | null;
  parent_message: number | null;
  latest_child_message: number | null;
  message: string;
  rephrased_query?: string | null;
  // Backend sends context_docs as a flat array of documents
  context_docs?: WorkplaceDocument[] | null;
  time_sent: string;
  overridden_model?: string;
  alternate_assistant_id?: number | null;
  chat_session_id: string;
  citations?: CitationMap | null;
  files: FileDescriptor[];
  tool_call?: ToolCallFinalResult | null;
  current_feedback?: string | null;
  // Duration in seconds for processing this message (agent messages only)
  processing_duration_seconds?: number;

  sub_questions?: SubQuestionDetail[];
  comments?: any;
  parentMessageId?: number | null;
  refined_answer_improvement?: boolean | null;
  is_agentic?: boolean | null;

  // NEW — CR-023 / SOL-023 backend §6.3 (GET /chat/get-chat-messages/:id).
  // `model` là model_version thô (vd. "claude-opus-4-6"), KHÔNG phải tên hiển thị.
  model?: string | null;
  model_provider?: string | null;
  // 0-based fan-out slot; null/absent = single-model message hoặc message cũ
  // (trước khi migration 019_multi_model_chat.sql chạy).
  model_index?: number | null;

  // Multi-model answer generation
  preferred_response_id?: number | null;
  model_display_name?: string | null;
  // Non-null when the model errored during generation
  error?: string | null;
}

export interface MessageResponseIDInfo {
  type: "message_id_info";
  user_message_id: number | null;
  reserved_assistant_message_id: number;
}

export interface ModelResponseSlot {
  message_id: number;
  model_name: string;
}

export interface MultiModelMessageResponseIDInfo {
  type: "multi_model_message_id_info";
  user_message_id: number | null;
  responses: ModelResponseSlot[];
}

export interface UserKnowledgeFilePacket {
  user_files: FileDescriptor[];
}

export interface DocumentsResponse {
  top_documents: WorkplaceDocument[];
  rephrased_query: string | null;
  level?: number | null;
  level_question_num?: number | null;
}

export interface FileChatDisplay {
  file_ids: string[];
}

export interface StreamingError {
  error: string;
  stack_trace?: string | null;
  error_code?: string;
  is_retryable?: boolean;
  details?: Record<string, any>;
}

export interface InputPrompt {
  id: number;
  prompt: string;
  content: string;
  active: boolean;
  is_public: boolean;
  user?: { id: string; email: string } | null;
}

export interface BaseQuestionIdentifier {
  level: number;
  level_question_num: number;
}

export interface SubQuestionDetail extends BaseQuestionIdentifier {
  question: string;
  answer: string;
  sub_queries?: SubQueryDetail[] | null;
  context_docs?: { top_documents: WorkplaceDocument[] } | null;
  is_complete?: boolean;
  is_stopped?: boolean;
  answer_streaming?: boolean;
}

export interface SubQueryDetail {
  query: string;
  query_id: number;
  doc_ids?: number[] | null;
}

// --- Non-chat, additive types kept from the app-stub era (unrelated features) ---

export interface ConnectorCredentialPairStatus {
  cc_pair_id: number;
  name: string;
  connector?: any;
  credential?: any;
  public_doc?: boolean;
  access_type?: string;
  status?: string;
}
